<template>
    <div
        class="b-modal"
        :class="[
            layoutClass,
            { 'has-no-padding': hasNoPadding || document, 'b-modal--document': document },
        ]"
    >
        <!-- <div class="b-modal__mask"></div> -->
        <div
            ref="dialog"
            class="b-modal__container"
            :style="modalStyle"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="dialogAriaLabelledBy"
            :aria-label="dialogAriaLabel"
            tabindex="-1"
            @keydown="handleKeydown"
        >
            <spin-loader
                v-if="isLoading"
                customClass="c-spin-loader-overlay"
            >
            </spin-loader>
            <div class="b-modal__topbar">
                <div class="b-modal__controls">
                    <icon-button
                        class="b-modal__close"
                        :class="[{ 'b-modal__close--backtrace': hasBacktrace }]"
                        color="white-primary"
                        icon="fa-times"
                        size="sm"
                        :tooltip="trans('common.close')"
                        @click="close"
                    >
                    </icon-button>
                    <icon-button
                        v-if="hasBacktrace"
                        class="b-modal__close b-modal__back"
                        color="primary"
                        size="sm"
                        icon="fa-arrow-left"
                        :tooltip="trans('common.back')"
                        @click="back"
                    >
                    </icon-button>
                </div>
                <div
                    id="modal_header"
                    class="b-modal__header"
                ></div>
            </div>
            <div
                ref="innerModal"
                id="modal_inner"
                class="b-modal__inner"
            >
                <slot></slot>
            </div>
            <div
                id="modal_footer"
                class="b-modal__footer"
            ></div>
        </div>
    </div>
</template>

<script>

import { nextTick } from 'vue';
import modelEventBus from '@js/nal/events/modelEvents.js';

export default {
    name: 'ModalComponent',
    provide: {
        isInModal: true,
    },
    emits: ['back', 'close'],
    props: {
        modalStyle: {
            type: Object,
            default: () => ({}),
            validator: (value) => {
                if (!(value instanceof Object)) {
                    return false;
                }

                const allowedProperties = ['width', 'height'];
                const valueProperties = Object.keys(value);

                // Check if the object only contains the properties 'width' and/or 'height'
                const isValid = valueProperties.every(prop => allowedProperties.includes(prop));

                if (!isValid) {
                    const disallowedProperties = valueProperties.filter(prop => !allowedProperties.includes(prop));
                    // eslint-disable-next-line no-console
                    console.warn('modalStyle contains disallowed properties:', disallowedProperties);
                }

                return isValid;
            },
        },
        document: Boolean,
        hasNoPadding: Boolean,
        isLoading: Boolean,
    },
    data() {
        return {
            hasHeaderContent: false,
            headerObserver: null,
            removeEscapeListener: null,
            currentModalView: null,
            previouslyFocusedElement: null,
            scrollLockState: null,
            siteWrapperElement: null,
            modelToTopHandler: null,
        };
    },
    computed: {
        layoutClass() {
            switch (this.nal?.project) {
                case 'client-portal':
                    return 'b-cp-layout';
                case 'nal':
                case 'executives':
                case 'home-ezekia':
                case 'home-nal':
                    return 'b-nal-layout';
                default:
                    return null;
            }
        },
        dialogAriaLabel() {
            return this.hasHeaderContent ? null : 'Dialog';
        },
        dialogAriaLabelledBy() {
            return this.hasHeaderContent ? 'modal_header' : null;
        },
        hasBacktrace() {
            return !!(this.nal.modal?.backtrace || []).length;
        },
    },
    methods: {
        close() {
            this.$emit('close');
        },
        back() {
            this.$emit('back');
        },
        closeHandler() {
            this.hasBacktrace ? this.back() : this.close();
        },
        saveScrollPositionOfPreviousModal() {
            const backtrace = this.nal.modal.backtrace;
            const isPreviousModal = this.nal.modal.scrollTop !== undefined;

            //Don't want to update last backtrace if we've gone back to a previous modal
            if (!isPreviousModal && backtrace?.length && this.$refs.innerModal) {
                backtrace[backtrace.length - 1].scrollTop = this.$refs.innerModal.scrollTop;
            }
        },
        applyScrollPosition() {
            this.$refs.innerModal.scrollTo(0, this.nal.modal.scrollTop || 0);
        },
        focusDialog() {
            this.$refs.dialog?.focus({ preventScroll: true });
        },
        getFocusableElements() {
            if (!this.$refs.dialog) {
                return [];
            }

            const selectors = [
                'a[href]',
                'area[href]',
                'button:not([disabled])',
                'input:not([disabled]):not([type="hidden"])',
                'select:not([disabled])',
                'textarea:not([disabled])',
                'iframe',
                'object',
                'embed',
                '[contenteditable="true"]',
                '[tabindex]:not([tabindex="-1"])',
            ];

            return Array.from(this.$refs.dialog.querySelectorAll(selectors.join(','))).filter((element) => {
                return !element.hasAttribute('disabled')
                    && element.getAttribute('aria-hidden') !== 'true'
                    && window.getComputedStyle(element).display !== 'none'
                    && window.getComputedStyle(element).visibility !== 'hidden';
            });
        },
        handleKeydown(event) {
            if (event.key !== 'Tab') {
                return;
            }

            const focusableElements = this.getFocusableElements();

            if (!focusableElements.length) {
                event.preventDefault();
                this.focusDialog();
                return;
            }

            const firstFocusableElement = focusableElements[0];
            const lastFocusableElement = focusableElements[focusableElements.length - 1];
            const isFocusedInsideDialog = this.$refs.dialog?.contains(document.activeElement);

            if (event.shiftKey) {
                if (document.activeElement === firstFocusableElement || !isFocusedInsideDialog) {
                    event.preventDefault();
                    lastFocusableElement.focus({ preventScroll: true });
                }

                return;
            }

            if (document.activeElement === lastFocusableElement || !isFocusedInsideDialog) {
                event.preventDefault();
                firstFocusableElement.focus({ preventScroll: true });
            }
        },
        observeHeader() {
            const headerElement = document.getElementById('modal_header');

            if (!headerElement) {
                return;
            }

            this.syncHeaderState();
            this.headerObserver = new MutationObserver(() => {
                this.syncHeaderState();
            });
            this.headerObserver.observe(headerElement, {
                characterData: true,
                childList: true,
                subtree: true,
            });
        },
        restorePreviousFocus() {
            if (this.previouslyFocusedElement instanceof HTMLElement && this.previouslyFocusedElement.isConnected) {
                this.previouslyFocusedElement.focus({ preventScroll: true });
            }
        },
        restoreStyleProperties(element, styles) {
            if (!element || !styles) {
                return;
            }

            Object.entries(styles).forEach(([property, value]) => {
                if (value) {
                    element.style.setProperty(property, value);
                    return;
                }

                element.style.removeProperty(property);
            });
        },
        applyModalScrollLock() {
            this.siteWrapperElement = document.getElementById('site_wrapper') || document.documentElement;
            const isSiteWrapper = this.siteWrapperElement.id === 'site_wrapper';
            const scrollTop = isSiteWrapper
                ? this.siteWrapperElement.scrollTop
                : window.scrollY;

            this.scrollLockState = {
                isSiteWrapper,
                scrollTop,
                body: {
                    left: document.body.style.getPropertyValue('left'),
                    overflow: document.body.style.getPropertyValue('overflow'),
                    'overscroll-behavior': document.body.style.getPropertyValue('overscroll-behavior'),
                    position: document.body.style.getPropertyValue('position'),
                    right: document.body.style.getPropertyValue('right'),
                    top: document.body.style.getPropertyValue('top'),
                    width: document.body.style.getPropertyValue('width'),
                },
                html: {
                    overflow: document.documentElement.style.getPropertyValue('overflow'),
                    'overscroll-behavior': document.documentElement.style.getPropertyValue('overscroll-behavior'),
                },
                siteWrapper: {
                    inset: this.siteWrapperElement.style.getPropertyValue('inset'),
                    overflow: this.siteWrapperElement.style.getPropertyValue('overflow'),
                    'overscroll-behavior': this.siteWrapperElement.style.getPropertyValue('overscroll-behavior'),
                    position: this.siteWrapperElement.style.getPropertyValue('position'),
                    top: this.siteWrapperElement.style.getPropertyValue('top'),
                    width: this.siteWrapperElement.style.getPropertyValue('width'),
                },
            };

            document.documentElement.classList.add('has-modal-open');
            document.documentElement.style.setProperty('overflow', 'hidden');
            document.documentElement.style.setProperty('overscroll-behavior', 'none');

            document.body.style.setProperty('overflow', 'hidden');
            document.body.style.setProperty('overscroll-behavior', 'none');

            if (isSiteWrapper) {
                const siteWrapperStyle = this.siteWrapperElement.style;
                siteWrapperStyle.setProperty('overflow', 'hidden');
                siteWrapperStyle.setProperty('overscroll-behavior', 'none');
                return;
            }

            document.body.style.setProperty('position', 'fixed');
            document.body.style.setProperty('top', `-${scrollTop}px`);
            document.body.style.setProperty('left', '0');
            document.body.style.setProperty('right', '0');
            document.body.style.setProperty('width', '100%');
        },
        removeModalScrollLock() {
            if (this.scrollLockState) {
                document.documentElement.classList.remove('has-modal-open');
                this.restoreStyleProperties(document.documentElement, this.scrollLockState.html);
                this.restoreStyleProperties(document.body, this.scrollLockState.body);
                this.restoreStyleProperties(this.siteWrapperElement, this.scrollLockState.siteWrapper);

                if (this.scrollLockState.isSiteWrapper) {
                    this.siteWrapperElement.scrollTop = this.scrollLockState.scrollTop;
                } else {
                    window.scrollTo(0, this.scrollLockState.scrollTop);
                }

                this.scrollLockState = null;
            }
        },
        syncHeaderState() {
            const headerElement = document.getElementById('modal_header');
            this.hasHeaderContent = !!headerElement?.textContent?.trim();
        },
    },
    watch: {
        $route() {
            this.close();
        },
    },
    created() {
        this.removeEscapeListener = window.handleEscapeListener(this.close);

        document.addEventListener('close-modal', this.closeHandler, false);
    },
    mounted() {
        this.previouslyFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        this.applyModalScrollLock();
        this.observeHeader();

        this.modelToTopHandler = () => {
            if (this.$refs.innerModal) {
                this.$refs.innerModal.scrollTop = 0;
            }
        };
        modelEventBus.$on('modalToTop', this.modelToTopHandler);
    },
    unmounted() {
        this.removeEscapeListener();
        document.removeEventListener('close-modal', this.closeHandler);

        this.removeModalScrollLock();
        this.headerObserver?.disconnect();
        this.restorePreviousFocus();

        if (this.modelToTopHandler) {
            modelEventBus.$off('modalToTop', this.modelToTopHandler);
        }

        this.nal.modal.isLoading = false;
    },
    beforeUpdate() {
        this.saveScrollPositionOfPreviousModal();
    },
    updated() {
        if (this.currentModalView && this.currentModalView !== this.nal.modal.component && this.$refs.innerModal) {
            this.applyScrollPosition();
        }

        this.syncHeaderState();

        this.currentModalView = this.nal.modal.component;
    },
};

</script>

<style lang="scss" scoped>
@use "sass:map";
@use "@sass/variables";
@use "@sass/mixins";

$padding: 20px;
$buttonGap: 45px;

.b-modal {
    background-color: rgb(0 0 0 / 40%);
    display: flex;
    flex-direction: row;
    height: 100%;
    inset: 0;
    justify-content: center;
    overflow: hidden;
    overscroll-behavior: none;
    position: fixed;
    width: calc(var(--site-wrapper-width) + var(--site-wrapper-scrollbar-width));
    z-index: mixins.z("modal");

    &__container {
        background-color: #fff;
        border-radius: 20px;
        box-shadow: 0 2px 8px rgb(0 0 0 / 33%);
        container-name: site-wrapper;
        container-type: inline-size;
        display: flex;
        flex-direction: column;
        font-size: 14px;
        margin: 5vh max(#{$buttonGap}, 3%);
        max-height: 90vh;
        max-width: calc(100% - max(#{2 * $buttonGap}, 6%));
        min-height: 20vh;
        position: relative;
        text-align: left;
        text-transform: none;
        width: 500px;
    }

    &__header {
        border-bottom: 1px solid variables.$var-color__grey--lighter;
        padding: $padding;

        &:empty {
            display: none;
        }

        :deep(h2) {
            font-size: map.get(variables.$rem-sizes, 'xl');
            line-height: normal;
            margin: 0;
        }
    }

    &__topbar {
        position: relative;
    }

    &__inner {
        border-radius: 20px;
        flex: 1;
        overflow: hidden auto;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain;
        padding: $padding;
        position: relative;
        /* stylelint-disable-next-line order/order */
        @include mixins.scrollbar($margin: 12px 0, $track-color: transparent);
    }

    &__footer {
        border-top: 1px solid variables.$var-color__grey--lighter;
        display: flex;
        flex-wrap: wrap-reverse;
        gap: $padding;
        justify-content: flex-end;
        padding: $padding;

        &:empty {
            display: none;
        }

        &:has(>.c-base-button.u-font-size--sm) {
            gap: variables.$size__sm--rem;
        }

        > :deep(div) {
            margin: 0;
        }
    }

    &__close {

        @include mixins.circle;
        border: none;
        box-shadow: 0 1px 6px rgb(0 0 0 / 20%);
        font-size: 14px;
        position: static !important;
        z-index: mixins.z("over");
    }

    &__controls {
        display: flex;
        flex-direction: column;
        gap: 8px;
        position: absolute;
        right: -38px;
        top: 0;
    }

    &.has-no-padding {
        .b-modal__inner {
            padding: 0;
        }
    }

    &--document {
        background-color: rgb(0 0 0 / 70%);
        overflow: hidden;
        padding: 61px 0 0;
        z-index: 12;

        .b-modal {
            &__topbar {
                padding: 0;
            }

            &__controls {
                position: absolute;
                right: 0;
                top: 0;
            }

            &__container {
                align-items: center;
                background-color: transparent;
                box-shadow: none;
                display: flex;
                height: calc(100vh - 60px);
                justify-content: center;
                margin: 0;
                max-height: calc(100vh - 60px);
                max-width: 100vw;
                padding: 0;
                width: 100%;
            }

            &__close {
                background: rgb(0 0 0 / 20%);
                border-left: solid 1px rgb(0 0 0 / 10%);
                border-radius: 0.25em;
                box-shadow: none;
                color: #fff;
                cursor: pointer;
                height: 60px;
                position: fixed !important;
                right: 0;
                top: 0;
                width: 60px;

                &:hover {
                    background: rgb(0 0 0 / 30%);
                }

                i {
                    font-size: 30px;
                }
            }

            &__back {
                right: 65px;
            }
        }
    }
}

@media (width <= #{variables.$breakpoint-lg}) {
    .b-modal--document {
        padding-top: 45px;

        .b-modal {
            &__close {
                height: 45px;
                width: 45px;
            }

            &__back {
                right: 50px;
            }
        }
    }
}

@media (width <= #{variables.$breakpoint-md}) {
    .b-modal {
        height: 100vh;
        height: 100dvh;
        width: 100vw;

        &__container {
            border-radius: 0;
            height: 100vh !important;
            height: 100dvh !important;
            margin: 0;
            max-height: 100vh !important;
            max-height: 100dvh !important;
            max-width: 100vw !important;
            min-height: 100vh !important;
            min-height: 100dvh !important;
            width: 100vw !important;
        }

        &__topbar {
            background-color: #fff;
            border-bottom: 1px solid variables.$var-color__grey--lighter;
            padding: calc(12px + env(safe-area-inset-top)) calc(16px + env(safe-area-inset-right)) 12px calc(16px + env(safe-area-inset-left));
            position: sticky;
            top: 0;
            z-index: mixins.z("over");
        }

        &__controls {
            flex-direction: row-reverse;
            margin-bottom: 12px;
            position: static;
        }

        &__topbar:has(.b-modal__header:empty) {
            .b-modal__controls {
                margin-bottom: 0;
            }
        }

        &__header {
            border-bottom: none;
            padding: 0;
        }

        &__inner {
            border-radius: 0;
            padding: $padding calc(#{$padding} + env(safe-area-inset-right)) calc(#{$padding} + env(safe-area-inset-bottom)) calc(#{$padding} + env(safe-area-inset-left));
        }

        &__footer {
            padding: $padding calc(#{$padding} + env(safe-area-inset-right)) calc(#{$padding} + env(safe-area-inset-bottom)) calc(#{$padding} + env(safe-area-inset-left));
        }
    }

    .b-modal--document {
        padding: 41px 0 0;

        .b-modal {
            &__container {
                height: calc(100vh - 40px) !important;
                max-height: calc(100vh - 40px) !important;
            }

            &__close {
                height: 40px;
                width: 40px;

                i {
                    font-size: 24px;
                }
            }
        }
    }
}

</style>
